import React from 'react';
import PropTypes from 'prop-types';
import './filter-session-annotation.css';

const POPUP_BASE_WIDTH = 280;
const POPUP_BASE_HEIGHT = 260;
const POSITION_OFFSET = 50;
const VERTICAL_CENTER_OFFSET = 80;
const MIN_TOP_OFFSET = 100;

const getPosition = (position, isCollapsed) => {
  const { x, y } = position;
  let left = x;
  let top = y;

  const remainingWidth = isCollapsed
    ? window.innerWidth - x
    : window.innerWidth - x - 300;
  const remainingHeight = window.innerHeight - y;

  if (remainingWidth <= POPUP_BASE_WIDTH) {
    left = x - POPUP_BASE_WIDTH;
  } else {
    left = x + POSITION_OFFSET;
  }

  if (remainingHeight <= POPUP_BASE_HEIGHT) {
    top = y - POPUP_BASE_HEIGHT;
  } else if (y <= MIN_TOP_OFFSET) {
    top = y;
  } else {
    top = y - VERTICAL_CENTER_OFFSET;
  }

  return { top, left };
};

/**
 * Annotation card displayed when clicking the Filter Session hub node.
 * Shows applied filters, result stats, and action buttons.
 */
const FilterSessionAnnotation = ({
  position,
  filterCount,
  resultCount,
  filters,
  isCollapsed,
  displayedCount,
  totalMatchCount,
  maxLimit,
}) => {
  const { top, left } = getPosition(position, isCollapsed);

  const style = {
    top: `${top}px`,
    left: `${left}px`,
    position: 'absolute',
  };

  // Parse filters JSON string if needed
  let parsedFilters = [];
  if (filters) {
    try {
      parsedFilters = typeof filters === 'string' ? JSON.parse(filters) : filters;
    } catch {
      parsedFilters = [];
    }
  }

  const formatFilterValue = (filter) => {
    const val = filter.value || filter.FilterValue;
    if (Array.isArray(val)) {
      return val.join(', ');
    }
    if (typeof val === 'number') {
      return val.toLocaleString();
    }
    return String(val || '—');
  };

  return (
    <div className="filter-session-annotation" style={style}>
      <div className="annotation-arrow" />
      <div className="filter-session-card">
        <div className="filter-card-header">
          <span className="header-title">
            <span role="img" aria-label="search">🔍</span> Filter Session
          </span>
          <span className="header-badge">
            {resultCount || 0} results
          </span>
        </div>
        <div className="filter-card-subheader">
          {filterCount || 0} filters · {resultCount || 0} results
        </div>

        {parsedFilters.length > 0 && (
          <div className="filter-card-section">
            <div className="filter-card-section-title">Applied Filters</div>
            <ul className="filter-list">
              {parsedFilters.map((f, idx) => (
                <li key={idx}>
                  <span className="filter-type">{f.type || f.Type}</span>
                  <span className="filter-value">{formatFilterValue(f)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="stats-row">
          <div className="stat">
            <span className="stat-value">{totalMatchCount ?? resultCount ?? 0}</span>
            <span className="stat-label">Total</span>
          </div>
          <div className="stat">
            <span className="stat-value">{displayedCount ?? resultCount ?? 0}</span>
            <span className="stat-label">Displayed</span>
          </div>
          <div className="stat">
            <span className="stat-value">{maxLimit ?? displayedCount ?? resultCount ?? 0}</span>
            <span className="stat-label">Max Limit</span>
          </div>
        </div>
      </div>
    </div>
  );
};

FilterSessionAnnotation.propTypes = {
  position: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
  }).isRequired,
  filterCount: PropTypes.number,
  resultCount: PropTypes.number,
  filters: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  isCollapsed: PropTypes.bool,
  displayedCount: PropTypes.number,
  totalMatchCount: PropTypes.number,
  maxLimit: PropTypes.number,
};

export default FilterSessionAnnotation;
